import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';
import uniqid from 'uniqid';

import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';
import startOfDecade from 'date-fns/startOfDecade';
import endOfDecade from 'date-fns/endOfDecade';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import getUnixTime from 'date-fns/getUnixTime';
import fromUnixTime from 'date-fns/fromUnixTime';
import { startOfDay } from 'date-fns';

import { appNow, appGetUnixTime } from '@/utils/timezone';

import { useSetupStore } from './setup';
import { useAuthStore } from './auth';
import TrialOnboardingApi from '@/store/api/system/trialOnboarding';
import InfoBoardApi from '@/store/api/system/infoBoard';
import UserApi from '@/store/api/system/user';
import DashboardApi from '@/store/api/system/dashboard';

// Grid uses 10 rows to fill the viewport height.
// cellHeight is calculated dynamically in the Dashboard component.
const FULL_HEIGHT = 10;

const REPEATABLE_WIDGET_TYPES = ['site-views-chart', 'shop-sales-chart', 'target-views-chart', 'referrer-chart'];

function getDaterangeForPeriod(period, mode) {
    const now = appNow();
    const isAggregate = mode === 'aggregate';

    switch (period) {
        case 'hour':
            return {
                start: appGetUnixTime(startOfDay(now)),
                end: appGetUnixTime(endOfDay(now)),
            };
        case 'week':
            if (isAggregate) {
                return {
                    start: appGetUnixTime(startOfWeek(now, { weekStartsOn: 1 })),
                    end: appGetUnixTime(endOfWeek(now, { weekStartsOn: 1 })),
                };
            }
            return {
                start: appGetUnixTime(startOfWeek(startOfMonth(now), { weekStartsOn: 1 })),
                end: appGetUnixTime(endOfWeek(endOfMonth(now), { weekStartsOn: 1 })),
            };
        case 'month':
            if (isAggregate) {
                return {
                    start: appGetUnixTime(startOfMonth(now)),
                    end: appGetUnixTime(endOfMonth(now)),
                };
            }
            return {
                start: appGetUnixTime(startOfYear(now)),
                end: appGetUnixTime(endOfYear(now)),
            };
        case 'year':
            if (isAggregate) {
                return {
                    start: appGetUnixTime(startOfYear(now)),
                    end: appGetUnixTime(endOfYear(now)),
                };
            }
            return {
                start: appGetUnixTime(startOfDecade(now)),
                end: appGetUnixTime(endOfDecade(now)),
            };
        default: // 'day'
            if (isAggregate) {
                return {
                    start: appGetUnixTime(startOfDay(now)),
                    end: appGetUnixTime(endOfDay(now)),
                };
            }
            return {
                start: appGetUnixTime(startOfWeek(now, { weekStartsOn: 1 })),
                end: appGetUnixTime(endOfWeek(now, { weekStartsOn: 1 })),
            };
    }
}

function getWidgetType(key) {
    const colonIndex = key.indexOf(':');
    if (colonIndex < 0) return key;
    return key.substring(0, colonIndex);
}

function findFirstKeyOfType(widgetKeys, type) {
    for (let i = 0; i < widgetKeys.length; i++) {
        if (getWidgetType(widgetKeys[i]) === type) return widgetKeys[i];
    }
    return null;
}

function findAllKeysOfType(widgetKeys, type) {
    return widgetKeys.filter(key => getWidgetType(key) === type);
}

function buildDefaultLayout(widgetKeys) {
    const statusLogsKey = findFirstKeyOfType(widgetKeys, 'status-logs-table');
    const firstTargetViewsKey = findFirstKeyOfType(widgetKeys, 'target-views-chart');
    const firstShopSalesKey = findFirstKeyOfType(widgetKeys, 'shop-sales-chart');
    const firstSiteViewsKey = findFirstKeyOfType(widgetKeys, 'site-views-chart');

    const hasStatusLogs = !!statusLogsKey;
    const hasTargetViews = !!firstTargetViewsKey;
    const hasShopSales = !!firstShopSalesKey;

    const contentHeight = FULL_HEIGHT;
    const layout = [];
    const placedKeys = {};

    // Determine right-side column width (shared between status-logs and target-views)
    const hasRightColumn = hasStatusLogs || hasTargetViews;
    const rightColumnWidth = hasRightColumn ? 4 : 0;
    const leftColumnWidth = hasRightColumn ? 8 : 12;

    const siteViewsHeight = hasShopSales ? Math.floor(contentHeight / 2) : contentHeight;

    if (firstSiteViewsKey) {
        layout.push({ id: firstSiteViewsKey, x: 0, y: 0, w: leftColumnWidth, h: siteViewsHeight, minW: 4, minH: 3 });
        placedKeys[firstSiteViewsKey] = true;
    }

    if (hasRightColumn) {
        if (hasStatusLogs && hasTargetViews) {
            // Both: split the right column vertically
            const topHeight = Math.floor(contentHeight / 2);
            const bottomHeight = Math.ceil(contentHeight / 2);
            layout.push({ id: statusLogsKey, x: leftColumnWidth, y: 0, w: rightColumnWidth, h: topHeight, minW: 3, minH: 3 });
            placedKeys[statusLogsKey] = true;
            layout.push({ id: firstTargetViewsKey, x: leftColumnWidth, y: topHeight, w: rightColumnWidth, h: bottomHeight, minW: 3, minH: 3 });
            placedKeys[firstTargetViewsKey] = true;
        } else if (hasStatusLogs) {
            layout.push({ id: statusLogsKey, x: leftColumnWidth, y: 0, w: rightColumnWidth, h: contentHeight, minW: 3, minH: 3 });
            placedKeys[statusLogsKey] = true;
        } else if (hasTargetViews) {
            layout.push({ id: firstTargetViewsKey, x: leftColumnWidth, y: 0, w: rightColumnWidth, h: contentHeight, minW: 3, minH: 3 });
            placedKeys[firstTargetViewsKey] = true;
        }
    }

    if (hasShopSales) {
        const salesWidth = hasRightColumn ? leftColumnWidth : 12;
        const salesHeight = Math.ceil(contentHeight / 2);
        const salesY = Math.floor(contentHeight / 2);
        layout.push({ id: firstShopSalesKey, x: 0, y: salesY, w: salesWidth, h: salesHeight, minW: 4, minH: 3 });
        placedKeys[firstShopSalesKey] = true;
    }

    // Place additional instances using same defaults as their widget type
    const typeDefaults = {
        'site-views-chart': { w: leftColumnWidth, h: siteViewsHeight, minW: 4 },
        'shop-sales-chart': { w: hasRightColumn ? leftColumnWidth : 12, h: Math.ceil(contentHeight / 2), minW: 4 },
        'target-views-chart': { w: rightColumnWidth || 4, h: contentHeight, minW: 3 },
        'status-logs-table': { w: rightColumnWidth || 4, h: contentHeight, minW: 3 },
        'referrer-chart': { w: 4, h: 5, minW: 3 },
    };
    let nextY = contentHeight;
    widgetKeys.forEach(key => {
        if (!placedKeys[key]) {
            const widgetType = getWidgetType(key);
            const defaults = typeDefaults[widgetType] || { w: 6, h: Math.floor(contentHeight / 2), minW: 4 };
            layout.push({ id: key, x: 0, y: nextY, w: defaults.w, h: defaults.h, minW: defaults.minW, minH: 3 });
            nextY += Math.floor(contentHeight / 2);
        }
    });

    return layout;
}

function mergeLayouts(savedLayout, currentWidgetKeys) {
    const filtered = savedLayout.filter(item => currentWidgetKeys.includes(item.id));

    const existingKeys = filtered.map(item => item.id);

    const defaultLayout = buildDefaultLayout(currentWidgetKeys);

    defaultLayout.forEach(defaultItem => {
        if (!existingKeys.includes(defaultItem.id)) {
            filtered.push(defaultItem);
        }
    });

    return filtered;
}

export const useDashboardStore = defineStore('dashboard', {
    state: function () {
        return {
            widgets: [],
            activeWidgetKeys: [],
            layout: [],
            standaloneWidgets: [],
            pendingInfoBoardContentIds: [],
            statisticsWidgetTypes: {},
        };
    },

    getters: {
        activeWidgets: function (state) {
            const filtered = state.widgets.filter(widget => {
                return !widget.standalone
                    && !widget.disabled
                    && state.activeWidgetKeys.indexOf(widget.key) >= 0;
            });
            // Sort by layout position (y then x) so DOM order matches for mobile GridStack
            const layout = state.layout;
            return filtered.sort((a, b) => {
                const layoutA = _.find(layout, { id: a.key }) || { y: 9999, x: 0 };
                const layoutB = _.find(layout, { id: b.key }) || { y: 9999, x: 0 };
                return (layoutA.y - layoutB.y) || (layoutA.x - layoutB.x);
            });
        },
        inactiveWidgets: function (state) {
            const standaloneKeys = state.standaloneWidgets.map(item => item.key);
            return state.widgets.filter(widget => {
                // Repeatable widgets (like "Add Info Board" and "Add Site Views Chart") always show in the sidebar
                if (widget.repeatable) {
                    return !widget.disabled;
                }
                // Statistics widget instances are managed via the repeatable creator
                if (widget.key && widget.key.indexOf(':') >= 0) {
                    const widgetType = getWidgetType(widget.key);
                    if (REPEATABLE_WIDGET_TYPES.indexOf(widgetType) >= 0) {
                        return false;
                    }
                }
                // Bare-key statistics widgets are hidden from sidebar (use creator instead)
                if (REPEATABLE_WIDGET_TYPES.indexOf(widget.key) >= 0) {
                    return false;
                }
                if (widget.standalone) {
                    return standaloneKeys.indexOf(widget.key) < 0 && !widget.disabled;
                }
                return !widget.disabled
                    && state.activeWidgetKeys.indexOf(widget.key) < 0;
            });
        },
        unavailableWidgets: function (state) {
            return state.widgets.filter(widget => widget.disabled === true);
        },
        standaloneWidgetsAbove: function (state) {
            return state.standaloneWidgets
                .filter(item => item.position === 'above')
                .map(item => _.find(state.widgets, { key: item.key }))
                .filter(Boolean);
        },
        standaloneWidgetsBelow: function (state) {
            return state.standaloneWidgets
                .filter(item => item.position === 'below')
                .map(item => _.find(state.widgets, { key: item.key }))
                .filter(Boolean);
        },
    },

    actions: {
        async init() {
            const setupStore = useSetupStore();
            const authStore = useAuthStore();
            const activeCms = setupStore.activeCms;
            const enabledExtensions = setupStore.enabledExtensions;

            // Permission check helper
            const hasReadPermission = (rightKey) => {
                if (authStore.isSuperUser || authStore.isOwner) return true;
                const rights = authStore.rights || [];
                const right = _.find(rights, { key: rightKey });
                return !!(right && _.get(right, 'permission.access'));
            };

            let domain;

            // get domain from cms->domains
            let domains = activeCms.domains || [];
            if (domains.length) domain = domains[0].base_url;

            let widgets = [];
            const defaultActiveKeys = [];

            // Check trial onboarding visibility for auto-activation
            const isSuperUser = authStore.isSuperUser;
            const isTrial = activeCms.status === 'trial' && activeCms.trial_ends_at && fromUnixTime(activeCms.trial_ends_at) > new Date();

            let showTrialOnboarding = false;

            if (isSuperUser) {
                showTrialOnboarding = true;
            } else if (isTrial) {
                try {
                    const content = await TrialOnboardingApi.fetch();
                    const hasContent = content && (
                        content.title ||
                        content.text ||
                        (content.media && content.media.length > 0)
                    );
                    showTrialOnboarding = hasContent;
                } catch (err) {
                    showTrialOnboarding = false;
                }
            }

            // Always register trial-onboarding widget (available in sidebar for all users)
            widgets.push({
                id: uniqid('new:'),
                name: 'Trial Onboarding',
                key: 'trial-onboarding',
                component: 'TrialOnboarding',
                standalone: true,
                global: true,
                settings: {}
            });

            // Register "Add Info Board" creator widget (only for owners/super users)
            const canCreateInfoBoard = isSuperUser || authStore.isOwner;
            if (canCreateInfoBoard) {
                widgets.push({
                    id: uniqid('new:'),
                    name: 'Info Board',
                    key: 'add-info-board',
                    component: null,
                    standalone: true,
                    repeatable: true,
                    settings: {}
                });
            }

            // Fetch and register Info Board instances for this CMS
            let infoBoardInstances = [];
            try {
                infoBoardInstances = await InfoBoardApi.fetchAll(activeCms.id);
            } catch (error) {
                infoBoardInstances = [];
            }

            infoBoardInstances.forEach(instance => {
                let instanceSettings = instance.settings;
                if (typeof instanceSettings === 'string') {
                    try { instanceSettings = JSON.parse(instanceSettings); } catch (e) { instanceSettings = {}; }
                }
                if (!instanceSettings) instanceSettings = {};

                widgets.push({
                    id: uniqid('new:'),
                    name: instance.title || 'Info Board',
                    key: 'info-board:' + instance.id,
                    component: 'InfoBoard',
                    standalone: true,
                    global: true,
                    contentId: parseInt(instance.id),
                    forceShow: instanceSettings.removable === false,
                    settings: instanceSettings
                });
            });

            const enabledSalesStats = enabledExtensions.includes('shop');
            const enabledStatusLogs = enabledExtensions.includes('status-logs');
            const enabledMarketing = enabledExtensions.includes('marketing');

            // Permission checks for statistics widgets
            const hasSiteViewsPermission = hasReadPermission('statistics:siteview-summaries');
            const hasStatusLogsPermission = enabledStatusLogs && hasReadPermission('statistics:siteview-summaries');
            const hasTargetViewsPermission = enabledMarketing && hasReadPermission('statistics:pageview-summaries');
            const hasShopSalesPermission = enabledSalesStats && hasReadPermission('statistics:sales-summaries');
            const shopCurrency = hasShopSalesPermission ? _.get(activeCms.shop, 'defaultCustomerGroup.currency.iso4217') : null;
            const shopCustomerGroups = hasShopSalesPermission ? _.get(activeCms.shop, 'customerGroups', []).filter(customerGroup => {
                return customerGroup.currency && customerGroup.currency.iso4217 === shopCurrency;
            }).map(customerGroup => customerGroup.id) : [];

            // Widget type definitions for statistics widgets
            const statisticsWidgetTypes = {
                'site-views-chart': {
                    name: 'All Pages Report',
                    component: 'SiteViewsChart',
                    disabled: !hasSiteViewsPermission,
                    defaultSettings: () => {
                        return {
                            period: 'hour',
                            start: appGetUnixTime(startOfDay(appNow(), { weekStartsOn: 1 })),
                            end: appGetUnixTime(endOfDay(appNow(), { weekStartsOn: 1 })),
                            height: 300,
                            domain: domain,
                        };
                    },
                },
                'target-views-chart': {
                    name: 'Marketing Target Views',
                    component: 'TargetViewsChart',
                    disabled: !hasTargetViewsPermission,
                    defaultSettings: () => {
                        return {
                            period: 'month',
                            start: appGetUnixTime(startOfMonth(appNow())),
                            end: appGetUnixTime(endOfMonth(appNow())),
                            height: 300,
                            domain: domain,
                        };
                    },
                },
                'shop-sales-chart': {
                    name: 'Shop Sales',
                    component: 'ShopSalesChart',
                    disabled: !hasShopSalesPermission,
                    defaultSettings: () => {
                        return {
                            period: 'day',
                            start: appGetUnixTime(startOfWeek(appNow(), { weekStartsOn: 1 })),
                            end: appGetUnixTime(endOfWeek(appNow(), { weekStartsOn: 1 })),
                            height: 300,
                            currency: shopCurrency,
                            customerGroups: shopCustomerGroups.slice(),
                        };
                    },
                },
                'referrer-chart': {
                    name: 'Referrers',
                    component: 'ReferrerChart',
                    disabled: !hasSiteViewsPermission,
                    defaultSettings: () => {
                        return {
                            period: 'month',
                            start: appGetUnixTime(startOfMonth(appNow())),
                            end: appGetUnixTime(endOfMonth(appNow())),
                            height: 300,
                            domain: domain,
                        };
                    },
                },
            };

            // Store for use by createStatisticsWidgetInstance
            this.statisticsWidgetTypes = statisticsWidgetTypes;

            // Register repeatable creator entries for statistics widgets (like add-info-board)
            REPEATABLE_WIDGET_TYPES.forEach(widgetType => {
                const typeDef = statisticsWidgetTypes[widgetType];
                widgets.push({
                    id: uniqid('new:'),
                    name: typeDef.name,
                    key: 'add-' + widgetType,
                    component: null,
                    repeatable: true,
                    widgetType: widgetType,
                    disabled: typeDef.disabled,
                    settings: {}
                });
            });

            // Register bare-key widgets (backward compat / default first instance)
            Object.keys(statisticsWidgetTypes).forEach(widgetType => {
                const typeDef = statisticsWidgetTypes[widgetType];
                widgets.push({
                    id: uniqid('new:'),
                    name: typeDef.name,
                    key: widgetType,
                    component: typeDef.component,
                    disabled: typeDef.disabled,
                    settings: !typeDef.disabled ? typeDef.defaultSettings() : {}
                });
                if (!typeDef.disabled) {
                    defaultActiveKeys.push(widgetType);
                }
            });

            // Status Logs — singleton, not repeatable
            widgets.push({
                id: uniqid('new:'),
                name: 'Status Logs',
                key: 'status-logs-table',
                component: 'StatusLogsTable',
                disabled: !hasStatusLogsPermission,
                settings: hasStatusLogsPermission ? {
                    period: 'day',
                    start: appGetUnixTime(subDays(startOfDay(appNow()), 6)),
                    end: appGetUnixTime(endOfDay(appNow())),
                    height: 300,
                    domain: domain,
                } : {}
            });
            if (hasStatusLogsPermission) {
                defaultActiveKeys.push('status-logs-table');
            }

            // Register instance widgets from saved active keys (keys with colon suffix)
            const savedActiveKeysForInstances = this.loadSavedActiveWidgetKeys(parseInt(activeCms.id));
            if (savedActiveKeysForInstances) {
                savedActiveKeysForInstances.forEach(key => {
                    if (key.indexOf(':') < 0) return; // bare key already registered
                    const widgetType = getWidgetType(key);
                    const typeDef = statisticsWidgetTypes[widgetType];
                    if (!typeDef || typeDef.disabled) return;

                    widgets.push({
                        id: uniqid('new:'),
                        name: typeDef.name,
                        key: key,
                        component: typeDef.component,
                        disabled: false,
                        settings: typeDef.defaultSettings()
                    });
                });
            }

            this.widgets = widgets;

            // Restore persisted widget settings (excludes daterange start/end)
            const savedWidgetSettings = this.loadSavedWidgetSettings(parseInt(activeCms.id));
            if (savedWidgetSettings) {
                const aggregateTypes = ['referrer-chart', 'target-views-chart'];
                widgets.forEach(widget => {
                    const saved = savedWidgetSettings[widget.key];
                    if (saved) {
                        Object.assign(widget.settings, saved);
                        // Recalculate daterange to match the restored period
                        if (saved.period) {
                            const widgetType = getWidgetType(widget.key);
                            const mode = aggregateTypes.indexOf(widgetType) >= 0 ? 'aggregate' : 'timeseries';
                            const daterange = getDaterangeForPeriod(saved.period, mode);
                            widget.settings.start = daterange.start;
                            widget.settings.end = daterange.end;
                        }
                    }
                });
            }

            // Build layout
            const enabledWidgetKeys = widgets.filter(widget => !widget.disabled && !widget.repeatable).map(widget => widget.key);
            const savedLayout = this.loadSavedLayout(parseInt(activeCms.id));
            const savedActiveKeys = this.loadSavedActiveWidgetKeys(parseInt(activeCms.id));

            const standaloneKeys = widgets.filter(widget => widget.standalone).map(widget => widget.key);
            const savedStandaloneWidgets = this.loadSavedStandaloneWidgets(parseInt(activeCms.id));

            if (savedActiveKeys) {
                // Filter out standalone and disabled widgets from active keys
                this.activeWidgetKeys = savedActiveKeys.filter(key => {
                    return standaloneKeys.indexOf(key) < 0 && enabledWidgetKeys.indexOf(key) >= 0;
                });
            } else {
                // Filter out standalone from active keys
                this.activeWidgetKeys = defaultActiveKeys.filter(key => {
                    return standaloneKeys.indexOf(key) < 0 && enabledWidgetKeys.indexOf(key) >= 0;
                });
            }

            // Restore standalone widgets
            if (savedStandaloneWidgets) {
                this.standaloneWidgets = savedStandaloneWidgets.filter(item => {
                    return standaloneKeys.indexOf(item.key) >= 0;
                });
            } else if (savedActiveKeys) {
                // Migration: old format stored trial-onboarding in activeWidgetKeys
                this.standaloneWidgets = [];
                if (savedActiveKeys.indexOf('trial-onboarding') >= 0) {
                    this.standaloneWidgets.push({ key: 'trial-onboarding', position: 'above' });
                }
            } else {
                // Default: show standalone widgets based on init logic
                this.standaloneWidgets = [];
                if (showTrialOnboarding) {
                    this.standaloneWidgets.push({ key: 'trial-onboarding', position: 'above' });
                }
            }

            // Force-show non-removable widgets for regular admins (not owner/superUser)
            if (!authStore.isOwner && !authStore.isSuperUser) {
                const forcedWidgets = widgets.filter(widget => widget.forceShow);
                forcedWidgets.forEach(widget => {
                    const alreadyVisible = _.find(this.standaloneWidgets, { key: widget.key });
                    if (!alreadyVisible) {
                        this.standaloneWidgets.push({ key: widget.key, position: 'above' });
                    }
                });
            }

            // Default: show new Info Board widgets that have no saved state
            if (!savedStandaloneWidgets) {
                infoBoardInstances.forEach(instance => {
                    const widgetKey = 'info-board:' + instance.id;
                    const alreadyVisible = _.find(this.standaloneWidgets, { key: widgetKey });
                    if (!alreadyVisible) {
                        this.standaloneWidgets.push({ key: widgetKey, position: 'above' });
                    }
                });
            }

            if (savedLayout) {
                // Filter out standalone widgets from saved layout
                const gridLayout = savedLayout.filter(item => standaloneKeys.indexOf(item.id) < 0);
                this.layout = mergeLayouts(gridLayout, this.activeWidgetKeys);
            } else {
                this.layout = buildDefaultLayout(this.activeWidgetKeys);
            }

            // Apply custom names from saved layout items to widget objects
            this.layout.forEach(item => {
                if (item.name) {
                    const widget = _.find(this.widgets, { key: item.id });
                    if (widget) Vue.set(widget, 'customName', item.name);
                }
            });

            // Apply custom names from saved standalone widget entries to widget objects
            this.standaloneWidgets.forEach(item => {
                if (item.name) {
                    const widget = _.find(this.widgets, { key: item.key });
                    if (widget) Vue.set(widget, 'customName', item.name);
                }
            });
        },

        loadSavedLayout(cmsId) {
            const authStore = useAuthStore();
            const layouts = _.get(authStore, 'user.settings.dashboard.layouts', []);
            if (!layouts || !layouts.length) return null;

            const numericCmsId = parseInt(cmsId);
            const cmsLayout = _.find(layouts, layout => parseInt(layout.cmsId) === numericCmsId);
            if (!cmsLayout || !cmsLayout.items || !cmsLayout.items.length) return null;

            return cmsLayout.items;
        },

        loadSavedActiveWidgetKeys(cmsId) {
            const authStore = useAuthStore();
            const layouts = _.get(authStore, 'user.settings.dashboard.layouts', []);
            if (!layouts || !layouts.length) return null;

            const numericCmsId = parseInt(cmsId);
            const cmsLayout = _.find(layouts, layout => parseInt(layout.cmsId) === numericCmsId);
            if (!cmsLayout || !cmsLayout.activeWidgetKeys) return null;

            return cmsLayout.activeWidgetKeys;
        },

        loadSavedWidgetSettings(cmsId) {
            const authStore = useAuthStore();
            const layouts = _.get(authStore, 'user.settings.dashboard.layouts', []);
            if (!layouts || !layouts.length) return null;

            const numericCmsId = parseInt(cmsId);
            const cmsLayout = _.find(layouts, layout => parseInt(layout.cmsId) === numericCmsId);
            if (!cmsLayout) return null;

            const settingsMap = {};

            const allSettings = [].concat(
                cmsLayout.siteViewsChartSettings || [],
                cmsLayout.shopSalesChartSettings || [],
                cmsLayout.targetViewsChartSettings || [],
                cmsLayout.referrerChartSettings || []
            );

            allSettings.forEach(entry => {
                if (!entry || !entry.widgetKey) return;
                const settings = _.omit(entry, ['widgetKey', '__typename']);
                // Remove null values
                Object.keys(settings).forEach(key => {
                    if (settings[key] === null || settings[key] === undefined) {
                        delete settings[key];
                    }
                });
                // Convert customerGroups IDs to strings to match el-option :value format
                if (settings.customerGroups) {
                    settings.customerGroups = settings.customerGroups.map(id => String(id));
                }
                settingsMap[entry.widgetKey] = settings;
            });

            return settingsMap;
        },

        loadSavedStandaloneWidgets(cmsId) {
            const authStore = useAuthStore();
            const layouts = _.get(authStore, 'user.settings.dashboard.layouts', []);
            if (!layouts || !layouts.length) return null;

            const numericCmsId = parseInt(cmsId);
            const cmsLayout = _.find(layouts, layout => parseInt(layout.cmsId) === numericCmsId);
            if (!cmsLayout || !cmsLayout.standaloneWidgets) return null;

            return cmsLayout.standaloneWidgets;
        },

        addWidget(key, position, index) {
            // Guard: cannot add disabled widgets
            const widget = _.find(this.widgets, { key: key });
            if (widget && widget.disabled) return;

            // Repeatable creator widgets trigger instance creation
            if (widget && widget.repeatable && key === 'add-info-board') {
                this.createInfoBoard(position, index);
                return;
            }

            // Repeatable statistics widget creators
            if (widget && widget.repeatable && widget.widgetType) {
                this.createStatisticsWidgetInstance(widget.widgetType);
                return;
            }

            // Standalone widgets are managed outside the grid
            if (widget && widget.standalone) {
                const existing = _.find(this.standaloneWidgets, { key: key });
                if (!existing) {
                    this.insertStandaloneAt(key, position || 'above', index);
                }
                return;
            }

            if (this.activeWidgetKeys.indexOf(key) >= 0) return;

            this.activeWidgetKeys.push(key);

            // Append to layout — GridStack will auto-place it
            const widgetType = getWidgetType(key);
            const typeDefaults = {
                'site-views-chart': { w: 8, h: 5, minW: 4 },
                'shop-sales-chart': { w: 8, h: 5, minW: 4 },
                'target-views-chart': { w: 4, h: 10, minW: 3 },
                'status-logs-table': { w: 4, h: 10, minW: 3 },
                'referrer-chart': { w: 4, h: 5, minW: 3 },
            };
            const defaults = typeDefaults[widgetType] || { w: 6, h: 5, minW: 4 };
            this.layout.push({
                id: key,
                x: 0,
                y: 9999,
                w: defaults.w,
                h: defaults.h,
                minW: defaults.minW,
                minH: 3,
            });
        },

        getPreviewLayout(widgetKey) {
            if (this.activeWidgetKeys.indexOf(widgetKey) >= 0) return this.layout;
            // Just return current layout — preview not needed without custom layout building
            return this.layout;
        },

        removeWidget(key) {
            const widget = _.find(this.widgets, { key: key });
            const authStore = useAuthStore();

            // Guard: only owner/superUser can remove forced (non-removable) widgets
            if (widget && widget.forceShow && !authStore.isOwner && !authStore.isSuperUser) return;

            // Standalone widgets are managed outside the grid
            if (widget && widget.standalone) {
                this.standaloneWidgets = this.standaloneWidgets.filter(item => item.key !== key);
                return;
            }

            const index = this.activeWidgetKeys.indexOf(key);
            if (index < 0) return;

            this.activeWidgetKeys.splice(index, 1);

            // Remove instance widgets from the widgets array (keys with colon suffix)
            if (key.indexOf(':') >= 0) {
                this.widgets = this.widgets.filter(w => w.key !== key);
            }

            // Remove from layout
            this.layout = this.layout.filter(item => item.id !== key);
        },

        moveStandaloneWidget(key, position, index) {
            // Remove existing entry first
            this.standaloneWidgets = this.standaloneWidgets.filter(item => item.key !== key);
            // Then insert at new position
            this.insertStandaloneAt(key, position, index);
        },

        insertStandaloneAt(key, position, index) {
            const aboveItems = this.standaloneWidgets.filter(item => item.position === 'above');
            const belowItems = this.standaloneWidgets.filter(item => item.position === 'below');

            const entry = { key: key, position: position };
            if (position === 'above') {
                const insertIndex = (index !== undefined) ? Math.min(index, aboveItems.length) : aboveItems.length;
                aboveItems.splice(insertIndex, 0, entry);
            } else {
                const insertIndex = (index !== undefined) ? Math.min(index, belowItems.length) : belowItems.length;
                belowItems.splice(insertIndex, 0, entry);
            }

            this.standaloneWidgets = aboveItems.concat(belowItems);
        },

        getLayoutItem(widgetKey) {
            const item = _.find(this.layout, { id: widgetKey });
            return item || { x: 0, y: 0, w: 12, h: FULL_HEIGHT, minW: 4, minH: 3 };
        },

        updateLayout(items) {
            const updatedIds = {};
            items.forEach(item => {
                updatedIds[item.id] = {
                    id: item.id,
                    x: item.x,
                    y: item.y,
                    w: item.w,
                    h: item.h,
                };
            });

            // Merge: update changed items, keep unchanged items intact
            this.layout = this.layout.map(existing => {
                if (updatedIds[existing.id]) {
                    const updated = updatedIds[existing.id];
                    updated.minW = existing.minW;
                    updated.minH = existing.minH;
                    updated.name = existing.name || null;
                    return updated;
                }
                return existing;
            });
        },

        buildLayoutEntry() {
            const setupStore = useSetupStore();
            const cmsId = parseInt(setupStore.activeCms.id);

            const siteViewsChartSettings = [];
            const shopSalesChartSettings = [];
            const targetViewsChartSettings = [];
            const referrerChartSettings = [];

            this.activeWidgetKeys.forEach(key => {
                const widgetType = getWidgetType(key);
                const widget = _.find(this.widgets, { key: key });
                if (!widget) return;

                const settings = _.get(widget, 'settings', {});

                if (widgetType === 'site-views-chart') {
                    siteViewsChartSettings.push({
                        widgetKey: key,
                        period: settings.period || null,
                        domain: settings.domain || null,
                        height: settings.height || null,
                        disabledDatasets: settings.disabledDatasets || null,
                        campaignVisitorsVisible: settings.campaignVisitorsVisible !== undefined ? settings.campaignVisitorsVisible : null,
                        campaignViewsVisible: settings.campaignViewsVisible !== undefined ? settings.campaignViewsVisible : null,
                    });
                } else if (widgetType === 'shop-sales-chart') {
                    const customerGroups = settings.customerGroups
                        ? settings.customerGroups.map(id => parseInt(id))
                        : null;
                    shopSalesChartSettings.push({
                        widgetKey: key,
                        period: settings.period || null,
                        currency: settings.currency || null,
                        customerGroups: customerGroups,
                        height: settings.height || null,
                        stacked: settings.stacked !== undefined ? settings.stacked : null,
                        disabledDatasets: settings.disabledDatasets || null,
                        campaignVisitorsVisible: settings.campaignVisitorsVisible !== undefined ? settings.campaignVisitorsVisible : null,
                        campaignViewsVisible: settings.campaignViewsVisible !== undefined ? settings.campaignViewsVisible : null,
                    });
                } else if (widgetType === 'target-views-chart') {
                    targetViewsChartSettings.push({
                        widgetKey: key,
                        period: settings.period || null,
                        domain: settings.domain || null,
                        height: settings.height || null,
                        stacked: settings.stacked !== undefined ? settings.stacked : null,
                        disabledDatasets: settings.disabledDatasets || null,
                        campaignVisitorsVisible: settings.campaignVisitorsVisible !== undefined ? settings.campaignVisitorsVisible : null,
                        campaignViewsVisible: settings.campaignViewsVisible !== undefined ? settings.campaignViewsVisible : null,
                    });
                } else if (widgetType === 'referrer-chart') {
                    referrerChartSettings.push({
                        widgetKey: key,
                        period: settings.period || null,
                        domain: settings.domain || null,
                        height: settings.height || null,
                    });
                }
            });

            return {
                cmsId: cmsId,
                activeWidgetKeys: this.activeWidgetKeys.slice(),
                standaloneWidgets: this.standaloneWidgets.map(item => ({ key: item.key, position: item.position, name: item.name || null })),
                items: this.layout.map(item => ({ id: item.id, x: item.x, y: item.y, w: item.w, h: item.h, name: item.name || null })),
                siteViewsChartSettings: siteViewsChartSettings,
                shopSalesChartSettings: shopSalesChartSettings,
                targetViewsChartSettings: targetViewsChartSettings,
                referrerChartSettings: referrerChartSettings,
            };
        },

        async saveDashboard(widgetEditData) {
            const authStore = useAuthStore();
            const setupStore = useSetupStore();
            const cmsId = parseInt(setupStore.activeCms.id);

            const layoutEntry = this.buildLayoutEntry();

            const trialOnboardingContent = _.get(widgetEditData, 'trialOnboardingContent', null);
            const infoBoardContents = _.get(widgetEditData, 'infoBoardContents', null);
            const removeInfoBoardIds = _.get(widgetEditData, 'removeInfoBoardIds', null);

            try {
                const result = await DashboardApi.save(
                    cmsId,
                    layoutEntry,
                    trialOnboardingContent,
                    infoBoardContents,
                    removeInfoBoardIds
                );

                // Update user settings with the new layout
                let settings = _.get(authStore, 'user.settings', {});
                if (!settings) settings = {};
                else settings = _.cloneDeep(settings);

                if (!settings.dashboard) settings.dashboard = {};
                if (!settings.dashboard.layouts) settings.dashboard.layouts = [];

                const layoutIndex = _.findIndex(settings.dashboard.layouts, layout => parseInt(layout.cmsId) === cmsId);

                if (layoutIndex >= 0) {
                    settings.dashboard.layouts[layoutIndex] = layoutEntry;
                } else {
                    settings.dashboard.layouts.push(layoutEntry);
                }

                authStore.updateUserSettings(settings);

                // Remove deleted info-board widgets from the widgets array
                if (removeInfoBoardIds && removeInfoBoardIds.length) {
                    removeInfoBoardIds.forEach(contentId => {
                        this.removeInfoBoardWidget(contentId);
                    });
                }

                return result;
            } catch (error) {
                console.error('Failed to save dashboard:', error);
                throw error;
            }
        },

        resetLayout() {
            // Reset standalone widgets — all enabled standalone widgets visible above
            // Exclude repeatable widgets (creator entries like "Add Info Board") — they are not real widgets
            this.standaloneWidgets = this.widgets
                .filter(widget => widget.standalone && !widget.disabled && !widget.repeatable)
                .map(widget => ({ key: widget.key, position: 'above' }));

            const enabledWidgetKeys = this.widgets.filter(widget => {
                return !widget.standalone && !widget.disabled && !widget.repeatable;
            }).map(widget => widget.key);

            this.activeWidgetKeys = enabledWidgetKeys.slice();
            this.layout = buildDefaultLayout(enabledWidgetKeys);
        },

        setWidgetValue({ widget, key, value }) {
            const index = _.findIndex(this.widgets, { id: widget.id });
            if (index >= 0) {
                Vue.set(this.widgets[index], key, value);
            }
        },

        createStatisticsWidgetInstance(widgetType) {
            const typeDef = this.statisticsWidgetTypes[widgetType];
            if (!typeDef) return null;

            const instanceId = uniqid();
            const instanceKey = widgetType + ':' + instanceId;

            this.widgets.push({
                id: uniqid('new:'),
                name: typeDef.name,
                key: instanceKey,
                component: typeDef.component,
                disabled: false,
                settings: typeDef.defaultSettings()
            });

            this.activeWidgetKeys.push(instanceKey);

            // Append to existing layout (preserves current positions)
            // GridStack will auto-place it at the next available position
            const typeDefaults = {
                'site-views-chart': { w: 8, h: 5, minW: 4 },
                'shop-sales-chart': { w: 8, h: 5, minW: 4 },
                'target-views-chart': { w: 4, h: 5, minW: 3 },
                'referrer-chart': { w: 4, h: 5, minW: 3 },
            };
            const defaults = typeDefaults[widgetType] || { w: 6, h: 5, minW: 4 };
            this.layout.push({
                id: instanceKey,
                x: 0,
                y: 9999,
                w: defaults.w,
                h: defaults.h,
                minW: defaults.minW,
                minH: 3,
            });

            return instanceKey;
        },

        async createInfoBoard(position, index) {
            const setupStore = useSetupStore();
            const cmsId = setupStore.activeCms ? setupStore.activeCms.id : null;
            if (!cmsId) return;

            const input = {
                title: '',
                subtitle: '',
                text: '',
                settings: JSON.stringify({ removable: true }),
                media: []
            };

            try {
                const instance = await InfoBoardApi.save(cmsId, input);
                this.addInfoBoardWidget(instance, position, index);
                this.pendingInfoBoardContentIds.push(parseInt(instance.id));
            } catch (error) {
                console.error('Failed to create info board:', error);
            }
        },

        confirmPendingInfoBoards() {
            this.pendingInfoBoardContentIds = [];
        },

        async cancelPendingInfoBoards() {
            const setupStore = useSetupStore();
            const cmsId = setupStore.activeCms ? setupStore.activeCms.id : null;

            this.pendingInfoBoardContentIds.forEach(contentId => {
                this.removeInfoBoardWidget(contentId);
                if (cmsId) {
                    InfoBoardApi.remove(contentId, cmsId).catch(() => {});
                }
            });

            this.pendingInfoBoardContentIds = [];
        },

        addInfoBoardWidget(instance, position, index) {
            let instanceSettings = instance.settings;
            if (typeof instanceSettings === 'string') {
                try { instanceSettings = JSON.parse(instanceSettings); } catch (e) { instanceSettings = {}; }
            }
            if (!instanceSettings) instanceSettings = {};

            const widgetKey = 'info-board:' + instance.id;

            // Add to widgets array
            this.widgets.push({
                id: uniqid('new:'),
                name: instance.title || 'Info Board',
                key: widgetKey,
                component: 'InfoBoard',
                standalone: true,
                global: true,
                contentId: parseInt(instance.id),
                forceShow: instanceSettings.removable === false,
                settings: instanceSettings
            });

            // Add to standalone widgets at specific position
            this.insertStandaloneAt(widgetKey, position || 'above', index);
        },

        removeInfoBoardWidget(contentId) {
            const widgetKey = 'info-board:' + contentId;

            // Remove from widgets array
            this.widgets = this.widgets.filter(widget => widget.key !== widgetKey);

            // Remove from standalone widgets
            this.standaloneWidgets = this.standaloneWidgets.filter(item => item.key !== widgetKey);
        },

        updateInfoBoardWidget(contentId, data) {
            const widgetKey = 'info-board:' + contentId;
            const widget = _.find(this.widgets, { key: widgetKey });
            if (!widget) return;

            let settings = data.settings;
            if (typeof settings === 'string') {
                try { settings = JSON.parse(settings); } catch (e) { settings = {}; }
            }
            if (!settings) settings = {};

            Vue.set(widget, 'name', data.title || 'Info Board');
            Vue.set(widget, 'forceShow', settings.removable === false);
            Vue.set(widget, 'settings', settings);
        },

        setWidgetCustomName(widgetKey, customName) {
            const normalizedName = customName || null;

            const widget = _.find(this.widgets, { key: widgetKey });
            if (widget) {
                Vue.set(widget, 'customName', normalizedName);
            }

            const layoutItem = _.find(this.layout, { id: widgetKey });
            if (layoutItem) {
                Vue.set(layoutItem, 'name', normalizedName);
            }

            const standaloneItem = _.find(this.standaloneWidgets, { key: widgetKey });
            if (standaloneItem) {
                Vue.set(standaloneItem, 'name', normalizedName);
            }
        },

        saveWidget() {
            // TODO: implement widget saving
        }
    }
});
